import React, { useEffect, useState } from 'react';
import { Bell, BellOff, BellRing } from 'lucide-react';
import { Button } from './ui/button';

/**
 * PushNotificationSetting
 *
 * Enables browser-native push notifications (Notification API) on WS
 * `new_message` events when the window is hidden/blurred. Stays 100 %
 * client-side — no service worker registration, no external push provider,
 * no tokens leaving the device. Matches the anti-surveillance ethos.
 *
 * Privacy-first payload: we never include decrypted message content or
 * sender-specific metadata beyond the contact's username. Literal body
 * reads "New encrypted message from <username>".
 *
 * Permission states:
 *   - `default`   → show the Enable button
 *   - `granted`   → show "Enabled" pill + Test button
 *   - `denied`    → show "Blocked in browser" hint (can't re-prompt)
 */
export const PushNotificationSetting = () => {
  const [supported] = useState(
    typeof window !== 'undefined' && 'Notification' in window
  );
  const [permission, setPermission] = useState(
    supported ? Notification.permission : 'unsupported'
  );

  // Re-sync permission on mount — covers the "user changed browser settings
  // while the tab was open" case.
  useEffect(() => {
    if (!supported) return;
    setPermission(Notification.permission);
  }, [supported]);

  const request = async () => {
    if (!supported) return;
    try {
      const p = await Notification.requestPermission();
      setPermission(p);
    } catch {}
  };

  const testFire = () => {
    if (!supported || permission !== 'granted') return;
    try {
      new Notification('Government Shield', {
        body: 'Push notifications are working.',
        silent: false,
        tag: 'ghostcomm-test',
      });
    } catch {}
  };

  if (!supported) {
    return (
      <div className="text-xs text-slate-500">
        Push notifications are not supported in this browser.
      </div>
    );
  }

  return (
    <div
      className="flex items-center gap-2 flex-wrap"
      data-testid="push-notification-setting"
    >
      {permission === 'granted' && (
        <>
          <span
            className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/30"
            data-testid="push-granted-pill"
          >
            <BellRing className="w-3 h-3" /> Enabled
          </span>
          <Button
            variant="ghost"
            size="sm"
            onClick={testFire}
            className="text-xs h-7"
            data-testid="push-test-btn"
          >
            Send a test
          </Button>
          <p className="basis-full text-[11px] text-slate-500 leading-relaxed">
            Fires while the window is hidden or another tab is active. We
            never include message content — just <em>"New encrypted message
            from &lt;sender&gt;"</em>.
          </p>
        </>
      )}
      {permission === 'default' && (
        <>
          <Button
            size="sm"
            onClick={request}
            className="text-xs h-7"
            data-testid="push-enable-btn"
          >
            <Bell className="w-3 h-3 mr-1" />
            Enable
          </Button>
          <p className="basis-full text-[11px] text-slate-500 leading-relaxed">
            Your browser will ask for permission. No tokens are sent anywhere
            — notifications are fired locally on WS events.
          </p>
        </>
      )}
      {permission === 'denied' && (
        <span
          className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-red-500/10 text-red-400 border border-red-500/30"
          data-testid="push-denied-pill"
        >
          <BellOff className="w-3 h-3" /> Blocked in browser
        </span>
      )}
    </div>
  );
};

export default PushNotificationSetting;
